// 品牌設定（LOGO、名稱、favicon）
//
// 刻意在「執行時」向 /brand/brand.json 取得，而不是編譯時寫死：
// 換 LOGO 只要替換 nginx/brand/ 底下的檔案再重新整理，
// 不用重新 build、不用重新部署，也不用每個院區各編一版。
//
// 取不到時退回下面的預設值，畫面不會因此壞掉。

import { useEffect, useState } from "react";

import { applyDefaultLang, translate, useLang } from "./i18n";

export const DEFAULT_BRAND = {
  appName: "",             // 空的 = 用目前語言的預設名稱（手术指导／手術指導／Surgical Guidance）
  logo: null,              // null = 顯示文字而非圖片
  favicon: null,
  showJitsiWatermark: true, // 會議畫面裡要不要顯示我們的 LOGO
};

const BRAND_URL = "/brand/brand.json";

let cached = null;

export const loadBrand = async () => {
  if (cached) return cached;
  try {
    const res = await fetch(BRAND_URL, { cache: "no-cache" });
    if (!res.ok) throw new Error(String(res.status));
    const json = await res.json();
    cached = { ...DEFAULT_BRAND, ...json };
    // 院方指定的預設語言（使用者自己選過就不蓋掉）
    if (cached.defaultLang) applyDefaultLang(cached.defaultLang);
  } catch {
    cached = DEFAULT_BRAND;
  }
  return cached;
};

/** 套用瀏覽器分頁標題與 favicon（index.html 裡的值是編譯時寫死的，這裡覆寫） */
const setHeadTag = (selector, create, attr, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const applyDocumentBrand = (brand) => {
  if (brand.appName) {
    document.title = brand.appName;
    // iOS「加入主畫面」預設的名稱
    setHeadTag("meta[name='apple-mobile-web-app-title']", () => {
      const m = document.createElement("meta");
      m.name = "apple-mobile-web-app-title";
      return m;
    }, "content", brand.appName);
  }
  if (brand.favicon) {
    setHeadTag("link[rel~='icon']", () => {
      const l = document.createElement("link");
      l.rel = "icon";
      return l;
    }, "href", brand.favicon);
  }
  // iOS 主畫面圖示。換LOGO.bat 會寫入帶版本號的網址（?v=…），
  // 舊的 brand.json 沒有這個欄位時就用固定路徑。
  setHeadTag("link[rel='apple-touch-icon']", () => {
    const l = document.createElement("link");
    l.rel = "apple-touch-icon";
    return l;
  }, "href", brand.touchIcon || "/brand/apple-touch-icon.png");
};

// 出廠的預設名稱：這幾個值視為「沒有自訂」，會跟著語言翻譯。
// 院方用 換LOGO.bat -Name 改成自己的名稱之後就原樣顯示，不翻。
const STOCK_NAMES = ["手術指導", "手术指导", "手术指導", "Surgical Guidance"];

/**
 * appName 可以是：
 *   字串             → 院方自訂名稱，所有語言都顯示這個
 *   物件             → { "zh-CN": "...", "zh-TW": "...", "en": "..." }，依語言挑
 *   空的／出廠預設值 → 依語言顯示預設名稱
 */
export const resolveAppName = (appName, lang) => {
  if (appName && typeof appName === "object") {
    return appName[lang] || appName["zh-CN"] || Object.values(appName)[0] || "";
  }
  if (!appName || STOCK_NAMES.includes(appName)) return translate(lang, "app.defaultName");
  return appName;
};

export const useBrand = () => {
  const [raw, setRaw] = useState(cached || DEFAULT_BRAND);
  const [lang] = useLang();

  useEffect(() => {
    let cancelled = false;
    loadBrand().then((b) => {
      if (!cancelled) setRaw(b);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const brand = { ...raw, appName: resolveAppName(raw.appName, lang) };

  // 名稱會隨語言改變，分頁標題也要跟著換
  useEffect(() => {
    applyDocumentBrand(brand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, lang]);

  return brand;
};
